import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import UserAction from "./actions/UserAction";
import TenantAction from "./actions/TenantAction";
import ProjectAction from "./actions/ProjectAction";
import AppLayout, { useQuery } from "./AppLayout";
import AppLicenseInfo from "./AppLicenseInfo";
import BucketAction from "./actions/BucketAction";
import ActivityAction from "./actions/ActivityAction";
import LayoutAction from "./actions/LayoutAction";
import { isGmailContext, isJiraContext } from "./api/api";
import LabelAction from "./actions/LabelAction";
import GoalAction from "./actions/GoalAction";
import BillingAction from "./actions/BillingAction";
import FilterAction from "./actions/FilterAction";
import DashboardAction from "./actions/DashboardAction";

interface MainPageProps {
  children?: React.ReactNode;
}

const MainPage = ({ children }: MainPageProps) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();
  const currentParams = useQuery();
  const contentType = currentParams.get("type") || "SALE";
  const jwt = currentParams.get("jwt");

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const pending = useSelector((state: any) => state.userReducer.get("pending"));
  const logged = useSelector((state: any) => state.userReducer.get("logged"));
  const accountId = currentUser.get("accountId");
  const language = currentUser.get("language");

  useEffect(() => {
    dispatch(UserAction.getCurrentUser());
    dispatch(TenantAction.getBuildInfo());
    dispatch(TenantAction.getTeamPipelines());
    dispatch(ProjectAction.getProjects());
    dispatch(BillingAction.getBillingUserLimits());
    dispatch(BillingAction.getBillingSubscriptionStatus());
    dispatch(BillingAction.getBillingPlans());
  }, [dispatch]);

  useEffect(() => {
    if (accountId) {
      dispatch(LayoutAction.countUnreadNotification(accountId));
    }
  }, [dispatch, accountId]);

  useEffect(() => {
    if (accountId) {
      dispatch(
        ActivityAction.countMyDueOverActivities({
          itemId: projectId,
          scope: "project",
          users: accountId,
          status: "false",
          eventActions: "createActivity",
        })
      );
    }
  }, [dispatch, accountId, projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(ProjectAction.getProject(projectId));
      dispatch(ProjectAction.getFavoriteTree(projectId));
      dispatch(GoalAction.getGoals(projectId));
      dispatch(DashboardAction.getDashboards(projectId));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectId && contentType) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
      dispatch(LabelAction.getLabels(projectId, contentType));
      dispatch(FilterAction.getMyFilters(projectId, contentType));
    }
  }, [dispatch, projectId, contentType]);

  useEffect(() => {
    if (!pending && !logged) {
      if (!isJiraContext() && !isGmailContext()) {
        window.location.href = "/app/login";
      }
    }
  }, [pending, logged]);

  useEffect(() => {
    if (jwt && jwt != "") {
      dispatch(UserAction.loginWithJwt(jwt));
    }
  }, [jwt, dispatch]);

  useEffect(() => {
    i18n.changeLanguage(language || "en");
  }, [language, i18n]);

  return (
    <>
      <AppLicenseInfo />
      <AppLayout>{children}</AppLayout>
    </>
  );
};

export default MainPage;
